import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2018 semantics GmbH
// Date        : $Date: 2026-05-25 21:10:12 +0200 (Mon, 25 May 2026) $
// Author      : Dirk Rothe, Olli Powroznik
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 163262 $
// Purpose     : ticker fulltext and highlight on page
// =============================================================================
// ---Third-party imports
import React from 'react';
import * as ReactDOM from 'react-dom';
import clsx from 'clsx';
import { Button } from 'semantic-ui-react';
// ---Framework imports
import log from 'uix/logging';
import { get, mapItems, values, sum, strip, isDigit, groupBy, isNil, items, uniq, flatten, dict, isString, size } from 'ldash';
import { hashObj } from 'uix/utils';
import { useState, useEffect, useInterval, useResettableState, useDelay, useApi, useMemo, useDebounce, useThrottledCallback, useSelector, useRef } from 'uix/hooks';
import { PageSetViewer } from 'uix/viewer/components';
import { useGeometryCalculation } from 'uix/viewer/hooks';
import { HYPHEN } from 'uix/modules/fulltext/constants';
import { Sel } from 'uix/registries';
// ---Local imports
import S from './textTicker.css';
import { GhostText } from './index';
import { useCaptionLink } from '../hooks';
// ---Exports
const ScrollToBottom = ({ height, children, textScrollPaddingBottom })=>{
    const scrollContainerRef = useRef();
    const bottomRef = useRef();
    const [contentHeight, setContentHeight] = useState(0);
    const scrollToBottom = useThrottledCallback(()=>{
        const scrollContainer = scrollContainerRef.current;
        const bottomElement = bottomRef.current;
        if (scrollContainer && bottomElement) {
            const bottomPosition = bottomElement.offsetTop - scrollContainer.offsetTop;
            scrollContainer.scrollTo({
                top: bottomPosition,
                behavior: 'smooth'
            });
        }
    }, 1000, {
        leading: true
    });
    useEffect(()=>{
        scrollToBottom();
    }, [
        children
    ]);
    useEffect(()=>{
        const scrollContainer = scrollContainerRef.current;
        if (scrollContainer) {
            const newContentHeight = scrollContainer.scrollHeight;
            if (newContentHeight !== contentHeight) {
                setContentHeight(newContentHeight);
                scrollToBottom();
            }
        }
    }, [
        children,
        contentHeight
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        ref: scrollContainerRef,
        style: {
            overflow: 'hidden',
            maxHeight: height,
            paddingBottom: textScrollPaddingBottom
        },
        children: [
            children,
            /*#__PURE__*/ _jsx("div", {
                ref: bottomRef
            })
        ]
    });
};
const useTick = ({ active, interval = 1000, limit = 60000 })=>{
    let [tick, setTick, resetTicker] = useResettableState(0, [
        interval,
        active
    ]);
    //~ useInterval(() => setTick(tick + interval), tick < interval ? interval : null)
    useInterval(()=>setTick(Math.min(limit, tick + interval)), active ? interval : null);
    return {
        msecs: tick,
        resetTicker
    };
};
const FadeText = ({ text })=>{
    let [style, setStyle] = useState({
        opacity: 0
    });
    useDelay(()=>setStyle({
            opacity: 1,
            transition: 'opacity 0.8s'
        }), [
        50
    ]);
    return /*#__PURE__*/ _jsx("span", {
        style: style,
        children: text
    });
};
const TickWord = ({ text, ws, tickCharsPerSec })=>{
    let [step, setStep] = useState(0);
    let interval = Math.floor(1 / tickCharsPerSec * 1000);
    useInterval(()=>setStep(step + 1), step <= text.length - 1 ? interval : null);
    return /*#__PURE__*/ _jsxs("span", {
        children: [
            ws,
            text.slice(0, step)
        ]
    });
};
const EMPTY_LIST = [];
const useWordTicker = ({ active, wdata, startIndex, stopIndex, pauses, tickCharsPerSec, sentenceEndSec, digitSec, commaSec })=>{
    let sentenceEndChars = Math.floor(sentenceEndSec * tickCharsPerSec);
    let digitChars = Math.floor(digitSec * tickCharsPerSec);
    let commaChars = Math.floor(commaSec * tickCharsPerSec);
    let { msecs } = useTick({
        interval: 50,
        limit: 120000,
        active
    });
    let wdata_words = wdata?.words || EMPTY_LIST;
    let { words, end_idx } = useMemo(()=>{
        let start_idx = startIndex;
        let end_idx = 0;
        let total_chars = 0;
        for (let [i, w] of wdata_words.slice(start_idx).entries()){
            let text = strip(w[4]);
            if (isDigit(text)) {
                total_chars += digitChars * text.length;
            } else {
                total_chars += text.length;
                if ([
                    '.',
                    '?',
                    '!'
                ].some((c)=>text.includes(c))) total_chars += sentenceEndChars;
                if ([
                    ',',
                    ';'
                ].some((c)=>text.includes(c))) total_chars += commaChars;
            }
            end_idx = start_idx + i;
            if (pauses?.[end_idx]) {
                total_chars += Math.floor(pauses[end_idx] * tickCharsPerSec);
            }
            if (end_idx >= stopIndex) {
                break;
            }
            if (total_chars / tickCharsPerSec * 1000 > msecs) {
                break;
            }
        }
        if (active && msecs > 0) {
            // we are waiting for the next word, so include it
            end_idx += 1;
        } // not active
        //~ log.x(active, end_idx)
        let words = wdata_words.slice(start_idx, end_idx);
        return {
            words,
            end_idx
        };
    }, [
        wdata_words,
        msecs
    ]);
    // if we use >= here, we miss the last word in finishedSeqs tracking
    let _finished = end_idx > stopIndex || end_idx > wdata_words.length - 1;
    // delay finished "true" signal to allow the last tick animation
    let last_word_ms = Math.floor(strip(String(wdata_words[stopIndex]?.[4])).length / tickCharsPerSec * 1000);
    let [finished] = useDebounce(_finished, last_word_ms, {
        leading: false
    });
    if (!_finished) {
        // only use debounced "true", do not debounce "false"
        finished = false;
    }
    //~ log.x(end_idx, stopIndex, wdata_words.length)
    let geom = useMemo(()=>words//~ .filter(w => strip(w[4]).length > 1) // skip punktuation
        .map(([x1, y1, x2, y2])=>({
                bbox: {
                    x1,
                    y1,
                    x2,
                    y2
                },
                className: S.highlight
            })), [
        words.at(-1)
    ]);
    let lineidx = words.at(-1)?.[5];
    return {
        words,
        geom,
        finished,
        lineidx,
        tickIdx: end_idx
    };
};
const useActive = ({ initialWaitSec = 0.1 })=>{
    let [active, setActive] = useResettableState(false, [
        initialWaitSec
    ]);
    useDelay(()=>setActive(true), initialWaitSec * 1000);
    //~ useDelay(() => setActive(false), 10000)
    return {
        active,
        setActive
    };
};
const isSequenceSwitch = (state)=>[
        'init',
        'next'
    ].includes(state);
const isActive = (state)=>[
        'running',
        'finished'
    ].includes(state);
const useSequenceIterator = ({ sequences, loading, started, initialWaitSec, finishedWaitSec, nextWaitSec })=>{
    let numSequences = sequences.length;
    let [seqIdx, setSeqIdx] = useState(0);
    /* Statemachine:
  init -> running -> finished -> next -> running ...
                              -> end
  */ let [state, setState] = useState('init');
    useEffect(()=>{
        if (!started) {
            setState('init');
            setSeqIdx(0);
        }
    }, [
        started,
        hashObj(sequences)
    ]);
    useDelay(()=>{
        if (!loading && started) {
            setState('running');
        }
    }, initialWaitSec * 1000, [
        loading,
        started
    ]);
    let active = isActive(state);
    if (sequences[seqIdx]?.finishedWaitSec) {
        // probably created by a pause on the last word
        finishedWaitSec = sequences[seqIdx].finishedWaitSec;
    }
    useDelay(()=>{
        if (state == 'finished') {
            ReactDOM.unstable_batchedUpdates(()=>{
                // goto next sequence item
                if (seqIdx < numSequences - 1) setSeqIdx(seqIdx + 1);
                if (seqIdx == numSequences - 1) {
                    // reached last, done
                    setState('end');
                } else {
                    setState('next');
                }
            });
        }
    }, finishedWaitSec * 1000, [
        state == 'finished'
    ]);
    useDelay(()=>{
        if (state == 'next') {
            setState('running');
        }
    }, nextWaitSec * 1000, [
        state == 'next'
    ]);
    return {
        seqIdx,
        setState,
        active,
        state
    };
};
const useWords = ({ otids, started, lineBreak })=>{
    let { data, loading } = useApi('getWords', {
        otids
    }, {
        condition: otids.length > 0 && started
    });
    let wordLists = useMemo(()=>{
        let res = data?.words || [];
        let lines = (words)=>{
            let ls = values(groupBy(words, (w)=>w[5]));
            // top/left from first word and bottom/left from last
            return ls.map((l)=>({
                    x1: l[0][0],
                    y1: l[0][1],
                    x2: l.at(-1)[2],
                    y2: l.at(-1)[3]
                }));
        };
        const transformWords = (words)=>{
            if (!lineBreak) {
                let hypenized_lineIdx = {};
                words = words.map((w, i)=>{
                    let is_last_word = !words[i + 1] || words[i + 1][5] > words[i][5];
                    let is_new_line = i > 0 && words[i - 1][5] < words[i][5];
                    let [x1, y1, x2, y2, text, lineIdx] = w;
                    // on line start, insert a whitespace before word
                    if (is_new_line && !hypenized_lineIdx[lineIdx - 1]) {
                        text = ` ${text}`;
                    }
                    // remove hyphens on last word in line
                    if (is_last_word && text.endsWith(HYPHEN)) {
                        text = text.slice(0, -1);
                        hypenized_lineIdx[lineIdx] = true;
                    }
                    return [
                        x1,
                        y1,
                        x2,
                        y2,
                        text,
                        lineIdx
                    ];
                });
            } else {
                // render in line mode
                words = words.map((w, i)=>{
                    let [x1, y1, x2, y2, text, lineIdx] = w;
                    // replace HYPHEN with '-'
                    if (text.endsWith(HYPHEN)) {
                        text = text.slice(0, -1) + '-';
                    }
                    return [
                        x1,
                        y1,
                        x2,
                        y2,
                        text,
                        lineIdx
                    ];
                });
            }
            return words;
        };
        res = dict(res.map(({ words, ...rest })=>[
                rest.otid,
                {
                    words: transformWords(words),
                    lines: lines(words),
                    ...rest
                }
            ]));
        return res;
    }, [
        data
    ]);
    return {
        wordLists,
        loading
    };
};
const useScrollTo = ({ state, wdata, wordBlock, lineidx, first_word_lineidx, scrollAlign, scrollFocusY, scrollDurationSec, scrollMarginTop })=>{
    let [scrollToPageRect, setScrollToPageRect] = useState(null);
    let isSwitch = isSequenceSwitch(state);
    let active = isActive(state);
    let minBlockLineIdx = wordBlock.at(0)?.[5];
    let maxBlockLineIdx = wordBlock.at(-1)?.[5];
    let getScrollRect = (i)=>{
        /* given a line index i, calculate zoomed area
     */ // scrollFocusY
        let { y1, y2 } = wdata.lines[i];
        if (scrollFocusY > 0) {
            // average over some lines above / below
            let lines = wdata.lines.slice(Math.max(0, minBlockLineIdx, i - scrollFocusY), i + 1 // + one line below current line
            );
            y1 = lines.at(0).y1;
            y2 = lines.at(-1).y1;
        }
        if (scrollMarginTop) {
            y1 = Math.max(0, y1 - scrollMarginTop);
        }
        // defaults
        let x1 = 0;
        let x2 = wdata.width;
        let lines = wdata.lines // all lines
        ;
        if (!isNil(minBlockLineIdx) && !isNil(maxBlockLineIdx)) {
            // only lines of wordBlock
            lines = wdata.lines.slice(minBlockLineIdx, maxBlockLineIdx);
        }
        // get left/right maximum
        if (lines.length > 0) {
            x1 = Math.min(...lines.map((l)=>l.x1)) - 50;
            x2 = Math.max(...lines.map((l)=>l.x2)) + 50;
        }
        return {
            x1,
            y1,
            x2,
            y2
        };
    };
    // scroll to next line during active sequence
    useEffect(()=>{
        if (!isNil(lineidx) && active && wdata) {
            let rect = getScrollRect(lineidx);
            //~ log.x('scroll line %o', rect, state, wdata.otid, lineidx)
            setScrollToPageRect({
                id: wdata.otid,
                ...rect,
                align: scrollAlign,
                scrollToMode: 'scroll',
                duration: scrollDurationSec
            });
        }
    }, [
        active,
        lineidx,
        wdata
    ]);
    // reposition to first/next sequence
    useEffect(()=>{
        if (isSwitch && !isNil(first_word_lineidx) && wdata) {
            //~ log.x('first line', state, wdata?.otid, first_word_lineidx)
            setScrollToPageRect({
                id: wdata.otid,
                ...getScrollRect(first_word_lineidx),
                align: scrollAlign,
                scrollToMode: 'scroll',
                duration: scrollDurationSec
            });
        }
    }, [
        isSwitch,
        first_word_lineidx,
        wdata
    ]);
    let debounceScrollTime = Math.floor((scrollDurationSec + 0.1) * 1000);
    let [_scrollToPageRect] = useDebounce(scrollToPageRect, debounceScrollTime, {
        leading: true
    });
    return _scrollToPageRect;
};
const decodeSequences = (sequences)=>{
    /* decode word ranges (.words) and pauses on sequence elements
   */ let decoded = sequences.map((s)=>{
        if (isNil(s.startIndex)) {
            s = {
                ...s,
                startIndex: 0
            };
        }
        let decodePauses = (pauses, startIndex, left, right)=>dict(pauses.split(',').map((s)=>strip(s)).filter(Boolean).map((s)=>{
                let [idx, seconds] = s.split(':');
                idx = parseInt(idx);
                seconds = parseFloat(seconds) || 1; // one second default
                // include a pause after the last index here
                if (idx < left || idx > right) return null;
                return [
                    startIndex + idx,
                    seconds
                ];
            }).filter(Boolean));
        if (!isString(s.words)) {
            // assume it's a simple startIndex/stopIndex range
            let pauses = decodePauses(String(s.pauses), s.startIndex, 0, 100000);
            return [
                {
                    ...s,
                    pauses
                }
            ];
        }
        // derive more sequences by splitting words (at ,)
        let decodeRange = (e)=>{
            let [l, r] = e.split(':');
            let [left, right] = [
                parseInt(l),
                parseInt(r)
            ];
            // support single index range
            if (!strip(r)) {
                right = left + 1;
            }
            if (left === NaN || right === NaN || right - left <= 0) {
                return false // for filter below
                ;
            }
            return [
                left,
                right
            ];
        };
        return s.words.split(',').filter((e)=>strip(e) && decodeRange(e)).map((e)=>{
            let [left, right] = decodeRange(e);
            let pauses = decodePauses(String(s.pauses), s.startIndex, left, right);
            let stopIndex = s.startIndex + right;
            // transform a pause at the last sequence word in a sequence transition delay
            let finishedWaitSec = pauses[stopIndex];
            return {
                ...s,
                startIndex: s.startIndex + left,
                stopIndex,
                pauses,
                finishedWaitSec
            };
        });
    });
    return flatten(decoded);
};
export const TextTicker = ({ sequences, description, mobile, caption, tickCharsPerSec, digitSec, sentenceEndSec, commaSec, scrollFocusY = 0, scrollMarginTop = 0, scrollDurationSec = 1, scrollAlign = 'center', textScrollPaddingBottom, initialWaitSec = 1, finishedWaitSec = 1, nextWaitSec = 1, height: outerHeight, started = false, playing = false, audio = false, viewerId = null, lineBreak, fontSize, fontStyle, hyphens, widths, orientation, linkid, debug = false })=>{
    // need at least otids in (unnormalized) sequences
    let otids = uniq(sequences.map((seq)=>seq.otid));
    let vid = `PageText_${viewerId}_${otids.join('_')}`;
    let { wordLists, loading } = useWords({
        otids,
        started,
        lineBreak
    });
    // normalize sequences
    sequences = useMemo(()=>decodeSequences(sequences), [
        hashObj(sequences)
    ]);
    //~ log.x(`post`, sequences)
    let { seqIdx, active, setState, state } = useSequenceIterator({
        sequences,
        loading,
        started,
        initialWaitSec,
        finishedWaitSec,
        nextWaitSec
    });
    // vl.pprint({state, active})
    let { otid, startIndex, stopIndex, pauses } = sequences[seqIdx] || {};
    let wdata = wordLists[otid];
    let apiurl = useSelector(Sel.apiurl);
    // sanitize stopIndex
    stopIndex = stopIndex || wdata?.words.length || 0;
    let wordBlock = wdata?.words.slice(startIndex, stopIndex) || [];
    let first_word_lineidx = wordBlock[0]?.[5] || 0;
    let [finishedSeqs, setFinishedSeqs] = useResettableState({}, [
        started
    ]);
    let { words, geom, finished, lineidx, tickIdx } = useWordTicker({
        active,
        wdata,
        startIndex,
        stopIndex,
        pauses,
        tickCharsPerSec,
        sentenceEndSec,
        digitSec,
        commaSec
    });
    useEffect(()=>{
        if (active && finished && started) {
            ReactDOM.unstable_batchedUpdates(()=>{
                setFinishedSeqs({
                    ...finishedSeqs,
                    [seqIdx]: {
                        otid,
                        words,
                        geom
                    }
                });
                setState('finished');
            });
        }
    }, [
        active,
        finished,
        started
    ]);
    useGeometryCalculation(vid, ()=>{
        // incomplte geometry of active sequence item
        let res = {
            [otid]: {
                wordticker: geom
            }
        };
        // inject finished geometry
        items(finishedSeqs).forEach(([idx, { otid, geom }])=>{
            if (idx == seqIdx && state != 'end') return;
            geom = geom.map((g)=>({
                    ...g,
                    className: S.finished
                }));
            let curr = res[otid] || {
                wordticker: []
            };
            res = {
                ...res,
                [otid]: {
                    wordticker: [
                        ...curr.wordticker,
                        ...geom
                    ]
                }
            };
        });
        return res;
    }, [
        geom,
        finishedSeqs,
        seqIdx,
        state
    ]);
    let scrollToPageRect = useScrollTo({
        state,
        wdata,
        wordBlock,
        lineidx,
        first_word_lineidx,
        scrollAlign,
        scrollFocusY,
        scrollDurationSec,
        scrollMarginTop
    });
    let text = useMemo(()=>{
        if (finishedSeqs[seqIdx]) return null;
        let lines = groupBy(words, (w)=>w[5]);
        return mapItems(lines, (lineidx, words, i)=>{
            let first_line = i == 0 // also checked in finishedTexts handling below
            ;
            let lb = !first_line && lineBreak;
            // if span instead of div, we add a ws at start of line
            return /*#__PURE__*/ _jsx("span", {
                className: clsx(lb && S.lineBreak),
                children: words.map((w, i)=>{
                    let is_last = i == words.length - 1;
                    if (is_last) {
                        let text = strip(w[4]);
                        let whitespace = w[4][0] == ' ' ? ' ' : '';
                        return /*#__PURE__*/ _jsx(TickWord, {
                            text: text,
                            ws: whitespace,
                            tickCharsPerSec: tickCharsPerSec
                        }, i);
                    } else {
                        return w[4];
                    }
                })
            }, lineidx);
        });
    }, [
        words,
        finishedSeqs,
        seqIdx
    ]);
    let finishedTexts = useMemo(()=>items(finishedSeqs).map(([idx, { words }])=>{
            // inject sequence seperator
            let sep = sequences[idx]?.sep;
            let draw_sep = null;
            // XXX: str / int compare
            let is_finished_last = idx == sequences.length - 1;
            if (sep && !is_finished_last) {
                draw_sep = sep;
            }
            let lines = groupBy(words, (w)=>w[5]);
            let lineDivs = mapItems(lines, (lineidx, words, i)=>{
                let last_line = i == size(lines) - 1;
                let first_line = i == 0 // also checked in ticking text handling above
                ;
                let lb = !first_line && !last_line && lineBreak;
                return /*#__PURE__*/ _jsxs("span", {
                    className: clsx(lb && S.lineBreak),
                    children: [
                        words.map((w)=>w[4]),
                        last_line ? draw_sep : null
                    ]
                }, lineidx);
            });
            return /*#__PURE__*/ _jsx("span", {
                children: lineDivs
            }, idx);
        }).filter(Boolean), [
        finishedSeqs
    ]);
    let viewerOpts = {
        // XXX: areaHeight does not align perfectly
        zoom: {
            mode: 'areaHeight'
        },
        quality: {
            placeholder: 'none'
        },
        mouse: {
            on: false
        },
        render: {
            paginations: 'off'
        }
    };
    let textElem = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            finishedTexts,
            text
        ]
    });
    let stopped = !playing && audio;
    let widthsArray = String(widths).split(',').filter((s)=>isDigit(strip(s))).map((s)=>parseInt(s));
    if (orientation == 'vertical') {
        widthsArray = [
            100,
            100
        ];
    }
    //~ log.x(wordBlock)
    let textProps = hyphens ? {
        lang: hyphens
    } : {};
    let textCssProps = hyphens ? {
        hyphens: 'auto'
    } : {};
    let captionLink = /*#__PURE__*/ _jsx(_Fragment, {
        children: useCaptionLink(caption, linkid, null, null, true)
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: clsx(S.textTicker, orientation == 'vertical' && S.vertical),
        style: {
            height: outerHeight
        },
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: clsx(S.pageViewer, playing && S.playing),
                style: {
                    height: outerHeight,
                    flex: `${widthsArray[0]}%`
                },
                children: [
                    /*#__PURE__*/ _jsx(PageSetViewer, {
                        vid: vid,
                        flex: true,
                        otids: otids,
                        activeLayers: [
                            'geometry'
                        ],
                        scrollToPageRect: scrollToPageRect,
                        options: viewerOpts,
                        loadOnVisible: true
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: S.description,
                        children: captionLink
                    })
                ]
            }),
            !mobile && /*#__PURE__*/ _jsxs("div", {
                ...textProps,
                className: S.text,
                style: {
                    flex: `${widthsArray[1]}%`,
                    fontSize,
                    fontStyle,
                    ...textCssProps
                },
                children: [
                    debug && /*#__PURE__*/ _jsx(DebugButton, {
                        data: {
                            seqIdx,
                            words,
                            finishedSeqs
                        }
                    }),
                    stopped ? /*#__PURE__*/ _jsx(GhostText, {
                        fontSize: fontSize
                    }) : /*#__PURE__*/ _jsx(ScrollToBottom, {
                        height: outerHeight,
                        textScrollPaddingBottom: textScrollPaddingBottom,
                        children: textElem
                    })
                ]
            })
        ]
    });
};
const DebugButton = ({ data })=>/*#__PURE__*/ _jsx(Button, {
        icon: "bug",
        floated: "right",
        onClick: ()=>{
            let words = data.words.map((w)=>w[4]);
            let finishedSeqs = values(data.finishedSeqs).map((s)=>s.words.map((w)=>w[4]));
            vl.pprint({
                words,
                finishedSeqs
            });
        },
        compact: true,
        size: "mini"
    });
